import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';

// Reuse existing upload function from the app
async function uploadFile(file) {
  const WORKER = import.meta.env.VITE_R2_UPLOAD_ENDPOINT || 'https://crm.gosnel.com/api/upload';
  const fd = new FormData();
  fd.append('file', file);
  fd.append('field', 'shared_document');
  const res = await fetch(WORKER, { method: 'POST', body: fd });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return (await res.json()).url;
}

/**
 * Document Management Page
 * Admin can upload files to R2 and control who can see them
 * Users can view files but cannot download
 */
export default function DocumentsPage() {
  const ctx = useContext(AppContext);
  const { currentUser, showToast } = ctx;
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(''); // For YouTube/external links
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [documentTitle, setDocumentTitle] = useState('');
  const [description, setDescription] = useState('');
  const [allowedRoles, setAllowedRoles] = useState(['admin']);
  const [documentCategory, setDocumentCategory] = useState('training'); // New category field
  const [viewingDoc, setViewingDoc] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null); // Document being edited
  const [expandedCategories, setExpandedCategories] = useState({
    training: true, // Training Path expanded by default
    resources: false,
    marketing: false,
    success: false,
    general: false
  });
  const [orderNumber, setOrderNumber] = useState(1); // For training material ordering

  // Category definitions with icons, labels, and sort order
  const CATEGORIES = [
    { 
      key: 'training', 
      label: '📚 التدريب', 
      icon: '🎓',
      color: '#10b981',
      description: 'مواد التدريب - تظهر في صفحة التدريب مع إكمال وتتبع',
      sortOrder: 1,
      isTraining: true // Flag to show special badge
    },
    { 
      key: 'resources', 
      label: 'الموارد والروابط', 
      icon: '🔗',
      color: '#8b5cf6',
      description: 'روابط المواقع والمنصات',
      sortOrder: 2
    },
    { 
      key: 'marketing', 
      label: 'الأصول التسويقية', 
      icon: '🎨',
      color: '#f59e0b',
      description: 'صور، أيقونات، وفيديوهات ترويجية',
      sortOrder: 3
    },
    { 
      key: 'success', 
      label: 'قصص النجاح والأسئلة الشائعة', 
      icon: '⭐',
      color: '#10b981',
      description: 'شهادات العملاء والأسئلة المتكررة',
      sortOrder: 4
    },
    { 
      key: 'general', 
      label: 'مستندات عامة', 
      icon: '📁',
      color: '#64748b',
      description: 'مستندات أخرى',
      sortOrder: 5
    }
  ];

  const ROLES = [
    { value: 'admin', label: 'المدير التنفيذي', icon: '👑' },
    { value: 'sales_manager', label: 'مدير مبيعات', icon: '🎯' },
    { value: 'marketer', label: 'منسّقة صفقات', icon: '📣' },
    { value: 'data_entry', label: 'مدخل بيانات', icon: '⌨️' },
    { value: 'accountant', label: 'محاسب', icon: '💼' },
    { value: 'rep', label: 'مندوب ميداني', icon: '🤝' },
  ];

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('shared_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter by user role
      const accessibleDocs = data.filter(doc => 
        doc.allowed_roles.includes(currentUser?.role)
      );

      // Sort documents: by category sortOrder, then by order_number (for training) or title
      const sortedDocs = accessibleDocs.sort((a, b) => {
        const catA = CATEGORIES.find(c => c.key === (a.category || 'general'));
        const catB = CATEGORIES.find(c => c.key === (b.category || 'general'));
        const sortA = catA?.sortOrder || 99;
        const sortB = catB?.sortOrder || 99;
        
        if (sortA !== sortB) return sortA - sortB;
        
        // Within same category, if training, sort by order_number
        if (a.category === 'training' && b.category === 'training') {
          return (a.order_number || 999) - (b.order_number || 999);
        }
        
        // Within same category, sort by title alphabetically
        return (a.title || '').localeCompare(b.title || '', 'ar');
      });

      setDocuments(sortedDocs);
    } catch (err) {
      console.error('Error loading documents:', err);
      showToast('❌ فشل في تحميل المستندات', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        showToast('❌ حجم الملف كبير جداً (الحد الأقصى 10MB)', 'error');
        return;
      }
      setSelectedFile(file);
      if (!documentTitle) {
        setDocumentTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const toggleRole = (role) => {
    setAllowedRoles(prev => 
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setDocumentTitle(doc.title);
    setDescription(doc.description || '');
    setAllowedRoles(doc.allowed_roles);
    setDocumentCategory(doc.category || 'general');
    setUploadType(doc.file_type === 'text/url' ? 'link' : 'file');
    setOrderNumber(doc.order_number || 1); // Load order number
    if (doc.file_type === 'text/url') {
      setDocumentUrl(doc.file_url);
    }
    setUploadModalOpen(true);
  };

  const handleUpload = async () => {
    if (!documentTitle || allowedRoles.length === 0) {
      showToast('❌ يرجى ملء جميع الحقول المطلوبة', 'error');
      return;
    }

    if (uploadType === 'link' && !documentUrl) {
      showToast('❌ يرجى إدخال الرابط', 'error');
      return;
    }

    if (uploadType === 'file' && !selectedFile) {
      showToast('❌ يرجى اختيار ملف', 'error');
      return;
    }

    setUploading(true);
    try {
      let fileUrl;
      let fileName = '';
      let fileType = '';
      let fileSize = 0;

      if (uploadType === 'link') {
        // Use the URL directly
        fileUrl = documentUrl;
        fileName = documentUrl;
        fileType = 'text/url';
        fileSize = 0;
      } else {
        // Upload file using existing upload function
        fileUrl = await uploadFile(selectedFile);
        fileName = selectedFile.name;
        fileType = selectedFile.type;
        fileSize = selectedFile.size;
      }

      // Save metadata to database
      let error;
      
      if (editingDoc) {
        // Update existing document
        const updateData = {
          title: documentTitle,
          description,
          category: documentCategory,
          allowed_roles: allowedRoles,
          order_number: orderNumber
        };
        
        // Only update file_url if it's a link or new file uploaded
        if (uploadType === 'link' && documentUrl !== editingDoc.file_url) {
          updateData.file_url = documentUrl;
          updateData.file_name = documentUrl;
          updateData.file_type = 'text/url';
          updateData.file_size = 0;
        } else if (uploadType === 'file' && selectedFile) {
          updateData.file_url = fileUrl;
          updateData.file_name = fileName;
          updateData.file_type = fileType;
          updateData.file_size = fileSize;
        }
        
        const result = await supabase
          .from('shared_documents')
          .update(updateData)
          .eq('id', editingDoc.id);
        
        error = result.error;
      } else {
        // Insert new document
        const result = await supabase.from('shared_documents').insert({
          title: documentTitle,
          description,
          category: documentCategory,
          file_url: fileUrl,
          file_name: fileName,
          file_type: fileType,
          file_size: fileSize,
          allowed_roles: allowedRoles,
          uploaded_by: currentUser.id,
          uploader_name: currentUser.name,
          order_number: orderNumber
        });
        
        error = result.error;
      }

      if (error) throw error;

      showToast(editingDoc ? '✅ تم تحديث المستند بنجاح' : '✅ تم حفظ المستند بنجاح');
      setUploadModalOpen(false);
      setEditingDoc(null);
      resetForm();
      loadDocuments();
    } catch (err) {
      console.error('Upload error:', err);
      showToast('❌ فشل في حفظ المستند: ' + err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setDocumentUrl('');
    setUploadType('file');
    setDocumentTitle('');
    setDescription('');
    setAllowedRoles(['admin']);
    setDocumentCategory('training');
    setOrderNumber(1);
    setEditingDoc(null);
  };

  const handleDelete = async (docId) => {
    if (!confirm('هل أنت متأكد من حذف هذا المستند؟')) return;

    try {
      const { error } = await supabase
        .from('shared_documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;

      showToast('✅ تم حذف المستند');
      loadDocuments();
    } catch (err) {
      console.error('Delete error:', err);
      showToast('❌ فشل في حذف المستند', 'error');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('word')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('csv')) return '📊';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isAdmin = currentUser?.role === 'admin';
  const canEditDocs = currentUser?.role === 'admin'; // Only admin can add/edit/delete docs

  return (
    <>
      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .documents-container {
            padding: 0 12px !important;
          }
          
          .category-header button {
            padding: 12px 14px !important;
          }
          
          .category-header span:first-child {
            font-size: 24px !important;
          }
          
          .document-card {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .document-card .doc-info {
            margin-bottom: 10px !important;
          }
          
          .document-card .doc-actions {
            width: 100% !important;
            justify-content: stretch !important;
          }
          
          .document-card .doc-actions button {
            flex: 1 !important;
          }
          
          .category-selector button {
            padding: 10px 12px !important;
          }
          
          .category-selector span:first-child {
            font-size: 20px !important;
          }
        }
      `}</style>

      <div className="documents-container" style={{ animation: 'sIn 0.3s ease' }}>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
            📁 المستندات المشتركة
          </h1>
          <p style={{ color: '#3d5470', fontSize: 13 }}>
            {canEditDocs ? 'إدارة ورفع المستندات للفريق' : 'المستندات المتاحة لك'}
          </p>
        </div>
        
        {canEditDocs && (
          <button
            onClick={() => setUploadModalOpen(true)}
            style={{
              background: 'linear-gradient(135deg, #059669, #10b981)',
              border: 'none',
              borderRadius: 10,
              padding: '10px 20px',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: "'Cairo',sans-serif",
              fontSize: 14,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            ➕ رفع مستند جديد
          </button>
        )}
      </div>

      {/* Documents List - Category Accordion */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#3d5470' }}>
          جارِ التحميل...
        </div>
      ) : documents.length === 0 ? (
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          padding: 60,
          textAlign: 'center',
          color: '#3d5470'
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>📁</div>
          <h3 style={{ color: '#000000', marginBottom: 8 }}>لا توجد مستندات</h3>
          <p>{isAdmin ? 'ابدأ برفع مستندات للفريق' : 'لا توجد مستندات متاحة لك حالياً'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {CATEGORIES.map(category => {
            // Filter documents for this category
            const categoryDocs = documents.filter(doc => 
              (doc.category || 'general') === category.key
            );
            
            // Skip category if no documents
            if (categoryDocs.length === 0) return null;
            
            const isExpanded = expandedCategories[category.key];
            
            return (
              <div
                key={category.key}
                className="category-accordion"
                style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  border: `1px solid ${isExpanded ? category.color + '44' : '#e2e8f0'}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Category Header - Clickable */}
                <button
                  onClick={() => toggleCategory(category.key)}
                  className="category-header"
                  style={{
                    width: '100%',
                    background: isExpanded ? `${category.color}11` : 'transparent',
                    border: 'none',
                    padding: '16px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontFamily: "'Cairo',sans-serif",
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span style={{ fontSize: 28 }}>{category.icon}</span>
                  <div style={{ flex: 1, textAlign: 'right' }}>
                    <div style={{ 
                      color: '#000000', 
                      fontSize: 16, 
                      fontWeight: 700,
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: 'flex-end'
                    }}>
                      {category.label}
                      {category.isTraining && (
                        <span style={{
                          background: '#10b981',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 12,
                        }}>
                          🎓 صفحة التدريب
                        </span>
                      )}
                      <span style={{ 
                        marginRight: 8,
                        color: category.color,
                        fontSize: 13,
                        fontWeight: 700
                      }}>
                        ({categoryDocs.length})
                      </span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{category.description}</div>
                  </div>
                  <span style={{ 
                    fontSize: 20, 
                    color: category.color,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}>
                    ▼
                  </span>
                </button>

                {/* Category Content - Expandable */}
                {isExpanded && (
                  <div style={{ 
                    borderTop: `1px solid ${category.color}22`,
                    padding: '12px 20px 16px',
                    display: 'grid',
                    gap: 10
                  }}>
                    {categoryDocs.map((doc, index) => (
                      <div
                        key={doc.id}
                        className="document-card"
                        style={{
                          background: '#ffffff',
                          borderRadius: 10,
                          border: '1px solid #e2e8f0',
                          padding: 14,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 14,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = category.color + '44';
                          e.currentTarget.style.transform = 'translateX(-4px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <div className="doc-info" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                          {/* Number badge for training category */}
                          {category.key === 'training' && (
                            <div style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: `${category.color}22`,
                              color: category.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 13,
                              fontWeight: 900,
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                          )}
                          
                          <span style={{ fontSize: 28 }}>{getFileIcon(doc.file_type)}</span>
                          <div style={{ textAlign: 'right' }}>
                            <h4 style={{ 
                              color: '#000000', 
                              fontSize: 14, 
                              fontWeight: 700, 
                              marginBottom: 4,
                              margin: '0 0 4px 0'
                            }}>
                              {doc.title}
                            </h4>
                            {doc.description && (
                              <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 6px 0' }}>
                                {doc.description}
                              </p>
                            )}
                            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#475569' }}>
                              <span>📅 {formatDate(doc.created_at)}</span>
                              {doc.file_size > 0 && <span>📦 {formatFileSize(doc.file_size)}</span>}
                              <span>👥 {doc.allowed_roles.map(r => ROLES.find(role => role.value === r)?.icon).join(' ')}</span>
                            </div>
                          </div>
                        </div>

                        <div className="doc-actions" style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => setViewingDoc(doc)}
                            style={{
                              background: `linear-gradient(135deg, ${category.color}, ${category.color}88)`,
                              border: 'none',
                              borderRadius: 8,
                              padding: '6px 14px',
                              color: '#fff',
                              cursor: 'pointer',
                              fontFamily: "'Cairo',sans-serif",
                              fontSize: 12,
                              fontWeight: 700
                            }}
                          >
                            👁️ عرض
                          </button>
                          
                          {canEditDocs && (
                            <button
                              onClick={() => handleEdit(doc)}
                              style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                border: 'none',
                                borderRadius: 8,
                                padding: '6px 12px',
                                color: '#fff',
                                cursor: 'pointer',
                                fontFamily: "'Cairo',sans-serif",
                                fontSize: 12,
                                fontWeight: 700
                              }}
                            >
                              ✏️
                            </button>
                          )}
                          
                          {canEditDocs && (
                            <button
                              onClick={() => handleDelete(doc.id)}
                              style={{
                                background: '#dc2626',
                                border: 'none',
                                borderRadius: 8,
                                padding: '6px 10px',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: 14
                              }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: '#0f172a',
            borderRadius: 16,
            border: '1px solid #ffffff',
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ color: '#000000', marginBottom: 20 }}>
              {editingDoc ? '✏️ تعديل المستند' : '➕ رفع مستند جديد'}
            </h2>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                عنوان المستند *
              </label>
              <input
                type="text"
                value={documentTitle}
                onChange={e => setDocumentTitle(e.target.value)}
                placeholder="مثال: دليل المبيعات 2024"
                style={{
                  width: '100%',
                  background: '#ffffff',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#000000',
                  fontSize: 14,
                  fontFamily: "'Cairo',sans-serif"
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                الوصف
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="وصف مختصر للمستند..."
                rows={3}
                style={{
                  width: '100%',
                  background: '#ffffff',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#000000',
                  fontSize: 14,
                  fontFamily: "'Cairo',sans-serif",
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                📂 الفئة *
              </label>
              <div className="category-selector" style={{ display: 'grid', gap: 8 }}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setDocumentCategory(cat.key)}
                    style={{
                      background: documentCategory === cat.key ? `${cat.color}22` : '#ffffff',
                      border: `2px solid ${documentCategory === cat.key ? cat.color : '#334155'}`,
                      borderRadius: 10,
                      padding: '12px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontFamily: "'Cairo',sans-serif",
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{cat.icon}</span>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ 
                        color: documentCategory === cat.key ? cat.color : '#f1f5f9', 
                        fontSize: 14, 
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        {cat.label}
                        {cat.isTraining && (
                          <span style={{
                            background: '#10b981',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 12,
                          }}>
                            🎓 صفحة التدريب
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 11 }}>{cat.description}</div>
                    </div>
                    {documentCategory === cat.key && (
                      <span style={{ color: cat.color, fontSize: 20 }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Order Number - Only for Training category */}
            {documentCategory === 'training' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                  🔢 رقم الترتيب *
                  <span style={{ color: '#64748b', fontSize: 12, marginRight: 8 }}>
                    (سيظهر بهذا الترتيب في صفحة التدريب)
                  </span>
                </label>
                <input
                  type="number"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(parseInt(e.target.value) || 1)}
                  min="1"
                  placeholder="1"
                  style={{
                    width: '100%',
                    padding: 12,
                    background: '#0f172a',
                    border: '2px solid #ffffff',
                    borderRadius: 10,
                    color: '#000000',
                    fontSize: 18,
                    fontWeight: 700,
                    fontFamily: "'Cairo',sans-serif",
                    textAlign: 'center'
                  }}
                />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                نوع المستند *
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setUploadType('file')}
                  style={{
                    flex: 1,
                    background: uploadType === 'file' ? '#059669' : '#ffffff',
                    border: `2px solid ${uploadType === 'file' ? '#10b981' : '#334155'}`,
                    borderRadius: 8,
                    padding: '12px',
                    color: uploadType === 'file' ? '#fff' : '#94a3b8',
                    cursor: 'pointer',
                    fontFamily: "'Cairo',sans-serif",
                    fontSize: 14,
                    fontWeight: 700
                  }}
                >
                  📄 ملف
                </button>
                <button
                  onClick={() => setUploadType('link')}
                  style={{
                    flex: 1,
                    background: uploadType === 'link' ? '#059669' : '#ffffff',
                    border: `2px solid ${uploadType === 'link' ? '#10b981' : '#334155'}`,
                    borderRadius: 8,
                    padding: '12px',
                    color: uploadType === 'link' ? '#fff' : '#94a3b8',
                    cursor: 'pointer',
                    fontFamily: "'Cairo',sans-serif",
                    fontSize: 14,
                    fontWeight: 700
                  }}
                >
                  🔗 رابط
                </button>
              </div>
            </div>

            {uploadType === 'file' ? (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                  الملف *
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                  style={{
                    width: '100%',
                    background: '#ffffff',
                    border: '1px solid #334155',
                    borderRadius: 8,
                    padding: 10,
                    color: '#000000'
                  }}
                />
                {selectedFile && (
                  <p style={{ color: '#10b981', fontSize: 13, marginTop: 8 }}>
                    ✅ {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                  الرابط *
                </label>
                <input
                  type="url"
                  value={documentUrl}
                  onChange={e => setDocumentUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... أو أي رابط آخر"
                  style={{
                    width: '100%',
                    background: '#ffffff',
                    border: '1px solid #334155',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#000000',
                    fontSize: 14,
                    fontFamily: "'Cairo',sans-serif",
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                />
                {documentUrl && documentUrl.includes('youtube.com') && (
                  <p style={{ color: '#10b981', fontSize: 12, marginTop: 6 }}>
                    ✅ سيتم تضمين فيديو YouTube في العارض
                  </p>
                )}
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
                من يمكنه المشاهدة؟ *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {ROLES.map(role => (
                  <button
                    key={role.value}
                    onClick={() => toggleRole(role.value)}
                    style={{
                      background: allowedRoles.includes(role.value) ? '#059669' : '#ffffff',
                      border: `2px solid ${allowedRoles.includes(role.value) ? '#10b981' : '#334155'}`,
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: allowedRoles.includes(role.value) ? '#fff' : '#94a3b8',
                      cursor: 'pointer',
                      fontFamily: "'Cairo',sans-serif",
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {role.icon} {role.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleUpload}
                disabled={uploading || !documentTitle || allowedRoles.length === 0 || (uploadType === 'file' && !selectedFile) || (uploadType === 'link' && !documentUrl)}
                style={{
                  flex: 1,
                  background: uploading || !documentTitle || allowedRoles.length === 0 || (uploadType === 'file' && !selectedFile) || (uploadType === 'link' && !documentUrl)
                    ? '#334155'
                    : 'linear-gradient(135deg, #059669, #10b981)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 20px',
                  color: '#fff',
                  cursor: uploading || !documentTitle || allowedRoles.length === 0 || (uploadType === 'file' && !selectedFile) || (uploadType === 'link' && !documentUrl)
                    ? 'not-allowed'
                    : 'pointer',
                  fontFamily: "'Cairo',sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  opacity: uploading || !documentTitle || allowedRoles.length === 0 || (uploadType === 'file' && !selectedFile) || (uploadType === 'link' && !documentUrl) ? 0.5 : 1
                }}
              >
                {uploading ? 'جارِ الحفظ...' : (editingDoc ? '💾 حفظ التغييرات' : '📤 رفع المستند')}
              </button>
              <button
                onClick={() => {
                  setUploadModalOpen(false);
                  resetForm();
                }}
                style={{
                  background: '#334155',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 20px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: "'Cairo',sans-serif",
                  fontSize: 14,
                  fontWeight: 700
                }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer (No Download) */}
      {viewingDoc && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}>
          {/* Viewer Header */}
          <div style={{
            background: '#0f172a',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #ffffff'
          }}>
            <h3 style={{ color: '#000000', margin: 0 }}>{viewingDoc.title}</h3>
            <button
              onClick={() => setViewingDoc(null)}
              style={{
                background: '#dc2626',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                color: '#fff',
                cursor: 'pointer',
                fontFamily: "'Cairo',sans-serif",
                fontSize: 14,
                fontWeight: 700
              }}
            >
              ✕ إغلاق
            </button>
          </div>

          {/* Viewer Content */}
          <div style={{ flex: 1, background: '#000' }}>
            {viewingDoc.file_type?.includes('pdf') ? (
              <iframe
                src={`${viewingDoc.file_url}#toolbar=0&navpanes=0&scrollbar=0`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={viewingDoc.title}
              />
            ) : viewingDoc.file_type?.includes('image') ? (
              <img
                src={viewingDoc.file_url}
                alt={viewingDoc.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none'
                }}
                onContextMenu={e => e.preventDefault()}
                draggable={false}
              />
            ) : viewingDoc.file_type === 'text/url' || viewingDoc.file_url?.includes('youtube.com') || viewingDoc.file_url?.includes('youtu.be') ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                background: '#0f172a',
                padding: 40
              }}>
                <div style={{ 
                  textAlign: 'center',
                  maxWidth: 600
                }}>
                  <div style={{ fontSize: 80, marginBottom: 20 }}>🔗</div>
                  <h2 style={{ color: '#000000', fontSize: 24, fontWeight: 900, marginBottom: 12 }}>
                    {viewingDoc.title}
                  </h2>
                  {viewingDoc.description && (
                    <p style={{ color: '#000000', fontSize: 16, marginBottom: 24 }}>
                      {viewingDoc.description}
                    </p>
                  )}
                  <a
                    href={viewingDoc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      background: 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
                      color: '#fff',
                      padding: '16px 32px',
                      borderRadius: 12,
                      fontSize: 18,
                      fontWeight: 700,
                      textDecoration: 'none',
                      fontFamily: "'Cairo',sans-serif",
                      transition: 'transform 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    🚀 فتح الرابط في نافذة جديدة
                  </a>
                  <p style={{ 
                    color: '#64748b', 
                    fontSize: 13, 
                    marginTop: 20,
                    wordBreak: 'break-all',
                    direction: 'ltr'
                  }}>
                    {viewingDoc.file_url}
                  </p>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#000000',
                fontSize: 18
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>📄</div>
                  <p>لا يمكن عرض هذا النوع من الملفات في المتصفح</p>
                  <a
                    href={viewingDoc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#4ecdc4',
                      fontSize: 16,
                      fontWeight: 700,
                      textDecoration: 'underline',
                      fontFamily: "'Cairo',sans-serif",
                    }}
                  >
                    فتح في نافذة جديدة
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
